import type { MapRef } from "@vis.gl/react-maplibre";
import { debounce } from "lodash";
import { LngLatBounds } from "maplibre-gl";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type RefObject,
} from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  areLngLatBoundsEqual,
  fitMapLibreBoundsSafely,
  getAliveMap,
  hasUsableMapContainer,
  padLngLatBounds,
  resizeMapLibreIfLaidOut,
} from "@ryde/controls";
import { useCompanyScopeContext } from "@ryde/redux";
import {
  autoFitMapAF,
  buildRoutesAutofitGeometrySignature,
  buildRoutesAutofitSelectionSignature,
  CORRECTING_FIX_BOUNDS_TIMEOUT,
  getAutofitBoundMapFromRoutes,
  GlobalAppEventEnum,
  IRideRoute,
  isValidCoordinateTuple,
  LANG_DEFAULT,
  liveMapAutofitPulseAtom,
  liveMapAutofitSuspendedAtom,
  MAP_AUTOFIT_DEBOUNCE_MS,
  MAP_PAD_RATIO,
  MAP_PAD_RATIO_CORRECTING,
  MAP_ROUTE_BLOCK_TYPES_WITH_BULK_AUTOFIT_SUSPEND,
  MapRouteBlockTypeEnum,
  PAD_AROUND_DIMENSION,
  profileDtoAtom,
  SupportedLanguage,
  useIsMobile,
} from "@ryde/shared";

export const useMapLibreRidesAutoFit = ({
  mapRef,
  routes,
  routeBlockType,
  isMapLoaded,
  isMapHidden,
}: {
  mapRef: RefObject<MapRef | null>;
  routes: IRideRoute[];
  routeBlockType: MapRouteBlockTypeEnum;
  isMapLoaded: boolean;
  isMapHidden?: boolean;
}): void => {
  const { eventEmitter } = useCompanyScopeContext();
  const { isMobile } = useIsMobile();
  const profile = useRecoilValue(profileDtoAtom);
  const autofitEnabled = useRecoilValue(autoFitMapAF(routeBlockType));
  const isBulkAutofitSuspendBlock =
    MAP_ROUTE_BLOCK_TYPES_WITH_BULK_AUTOFIT_SUSPEND.has(routeBlockType);
  const bulkAutofitSuspended = useRecoilValue(liveMapAutofitSuspendedAtom);
  const bulkAutofitPulse = useRecoilValue(liveMapAutofitPulseAtom);
  const isAutofitSuspended = isBulkAutofitSuspendBlock && bulkAutofitSuspended;
  const langRef = useRef<SupportedLanguage | undefined>(undefined);
  const correctingBoundsRef = useRef<LngLatBounds | undefined>(undefined);
  const lastFittedBoundsRef = useRef<LngLatBounds | undefined>(undefined);
  const pendingBoundsRef = useRef<LngLatBounds | undefined>(undefined);

  const routesAutofitGeometrySignature = useMemo(
    () => buildRoutesAutofitGeometrySignature(routes),
    [routes],
  );
  const routesAutofitSelectionSignature = useMemo(
    () => buildRoutesAutofitSelectionSignature(routes),
    [routes],
  );
  // RYDE-7837: the live map re-fits only when the SELECTED ride set changes. A poll
  // moves each car's GPS point, which the geometry signature would treat as a change
  // and re-fit (zooming out on every refresh) — so LiveMap uses the ids-only
  // selection signature and keeps the user's zoom until they change the selection.
  // Every other block type keeps the geometry signature (so a route edit still re-fits).
  const routesAutofitSignature =
    routeBlockType === MapRouteBlockTypeEnum.LiveMap
      ? routesAutofitSelectionSignature
      : routesAutofitGeometrySignature;

  useEffect(() => {
    if (!routesAutofitSignature) {
      lastFittedBoundsRef.current = undefined;
      pendingBoundsRef.current = undefined;
    }
  }, [routesAutofitSignature]);

  useEffect(() => {
    if (isMapHidden) {
      lastFittedBoundsRef.current = undefined;
    }
  }, [isMapHidden]);

  const boundsComputed = useMemo(() => {
    if (!routesAutofitSignature) {
      return undefined;
    }

    const boundMap = getAutofitBoundMapFromRoutes(routes);
    const validBoundMap = boundMap.filter(isValidCoordinateTuple);

    if (!validBoundMap.length) {
      return undefined;
    }

    const bounds = new LngLatBounds();
    validBoundMap.forEach(([lat, lng]) => {
      bounds.extend([lng, lat]);
    });

    correctingBoundsRef.current = padLngLatBounds(
      bounds,
      MAP_PAD_RATIO_CORRECTING,
    );
    return padLngLatBounds(bounds, MAP_PAD_RATIO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routesAutofitSignature]);

  const fitBoundsIfChanged = useEvent(
    (
      bounds: LngLatBounds | undefined,
      options?: { resize?: boolean; force?: boolean },
    ): void => {
      const map = getAliveMap(mapRef.current);

      if (!map || !bounds || !autofitEnabled || !isMapLoaded || isMapHidden) {
        pendingBoundsRef.current = bounds;
        return;
      }

      if (!hasUsableMapContainer(mapRef)) {
        pendingBoundsRef.current = bounds;
        return;
      }

      if (
        !options?.force &&
        areLngLatBoundsEqual(bounds, lastFittedBoundsRef.current)
      ) {
        return;
      }

      if (options?.resize) {
        resizeMapLibreIfLaidOut(map);
      }

      if (
        fitMapLibreBoundsSafely({
          map,
          bounds,
          padding: PAD_AROUND_DIMENSION,
        })
      ) {
        lastFittedBoundsRef.current = bounds;
        pendingBoundsRef.current = undefined;
        return;
      }

      // Fit returned false (container too small, or camera restored after silent NaN). Keep the bounds
      // so the next resize finishes the framing instead of treating it as already done.
      pendingBoundsRef.current = bounds;
    },
  );

  const invalidateMapSizeCore = useEvent((): void => {
    // Match the canvas to its container. This is all a resize may do: the viewport changed, the SELECTION did
    // not, so the framing is still the user's to own.
    resizeMapLibreIfLaidOut(getAliveMap(mapRef.current));

    // The one exception is a fit that was already requested and could not run — the map was hidden, not yet
    // loaded, or its container had no size yet, so `fitBoundsIfChanged` parked the bounds here. Completing it
    // is finishing that fit, not starting a new one.
    //
    // It deliberately does NOT fall back to the current selection's bounds any more. Doing so re-fitted on
    // every container resize (drawer open/close, panel drag, window resize), throwing away whatever the user
    // had panned and zoomed to. Only a new ride selection changes the bounds now.
    const pendingBounds = pendingBoundsRef.current;

    if (!pendingBounds) {
      return;
    }

    fitBoundsIfChanged(pendingBounds, { force: true });
  });

  const debouncedAutofit = useMemo(
    () =>
      debounce((bounds: LngLatBounds | undefined) => {
        fitBoundsIfChanged(bounds, { resize: true });
      }, MAP_AUTOFIT_DEBOUNCE_MS),
    [fitBoundsIfChanged],
  );

  const debouncedInvalidateMapSize = useMemo(
    () =>
      debounce(() => {
        invalidateMapSizeCore();
      }, MAP_AUTOFIT_DEBOUNCE_MS),
    [invalidateMapSizeCore],
  );

  const invalidateMapSize = useEvent((): void => {
    debouncedInvalidateMapSize();
  });

  useEffect(() => {
    return () => {
      debouncedAutofit.cancel();
      debouncedInvalidateMapSize.cancel();
    };
  }, [debouncedAutofit, debouncedInvalidateMapSize]);

  useEffect(() => {
    if (isAutofitSuspended) {
      return;
    }

    debouncedAutofit(boundsComputed);
    return () => {
      debouncedAutofit.cancel();
    };
  }, [
    boundsComputed,
    debouncedAutofit,
    autofitEnabled,
    isAutofitSuspended,
    bulkAutofitPulse,
    isMapLoaded,
    isMapHidden,
  ]);

  useEffect(() => {
    eventEmitter?.on(GlobalAppEventEnum.RESIZE_RIDE_MAP, invalidateMapSize);

    return () => {
      eventEmitter?.off(GlobalAppEventEnum.RESIZE_RIDE_MAP, invalidateMapSize);
    };
  }, [eventEmitter, invalidateMapSize]);

  useEffect(() => {
    const lang = profile?.metadata?.language || LANG_DEFAULT;
    const hasLanguageChanged = Boolean(
      langRef.current && langRef.current !== lang,
    );

    langRef.current = lang;

    if (!hasLanguageChanged) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (correctingBoundsRef.current) {
        fitBoundsIfChanged(correctingBoundsRef.current, {
          resize: true,
          force: true,
        });
      }
    }, CORRECTING_FIX_BOUNDS_TIMEOUT);

    // Cancel the pending refit so it cannot run after unmount.
    return () => {
      clearTimeout(timeoutId);
    };
  }, [boundsComputed, fitBoundsIfChanged, profile?.metadata?.language]);

  const observerCallback: ResizeObserverCallback = useEvent(
    (resizeEntries: ResizeObserverEntry[]) => {
      window.requestAnimationFrame((): void => {
        if (!Array.isArray(resizeEntries) || !resizeEntries.length) {
          return;
        }

        if (isAutofitSuspended) {
          return;
        }

        debouncedInvalidateMapSize();
      });
    },
  );

  useLayoutEffect(() => {
    if (isMobile || !isMapLoaded) {
      return;
    }

    const containerElem = getAliveMap(mapRef.current)?.getContainer();

    if (!containerElem) {
      return;
    }

    const resizeObserver = new ResizeObserver(observerCallback);
    resizeObserver.observe(containerElem);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isMobile, isMapLoaded, mapRef, observerCallback]);
};
